import type { CmsEditorCanvasProps } from './types'
import BuilderCanvas from '@/components/cms/BuilderCanvas'
import VariantPicker from '@/components/cms/VariantPicker'

export default function CmsEditorCanvas({
  canvasBlocks,
  selectedBlockId,
  selectedBlockIds,
  canvasZoom,
  deviceMode,
  showGrid,
  localSections,
  variantPickerTarget,
  showShortcuts,
  onSelectBlock,
  onSetLeftPanelMode,
  onReorderBlocks,
  onAddBlock,
  onRemoveBlock,
  onDuplicateBlock,
  onResizeBlock,
  onUpdateBlock,
  onVariantSelected,
  onCloseVariantPicker,
  onCloseShortcuts,
}: CmsEditorCanvasProps) {
  return (
    <>
      {/* ── Live Canvas (Card-based grid) ──────── */}
      <BuilderCanvas
        blocks={canvasBlocks}
        selectedBlockId={selectedBlockId}
        selectedBlockIds={selectedBlockIds}
        zoom={canvasZoom}
        deviceMode={deviceMode}
        showGrid={showGrid}
        onSelectBlock={(id, shiftKey) => {
          onSelectBlock(id, shiftKey)
          if (id && !shiftKey) onSetLeftPanelMode('layers')
        }}
        onReorder={onReorderBlocks}
        onAddBlock={onAddBlock}
        onRemoveBlock={onRemoveBlock}
        onDuplicateBlock={onDuplicateBlock}
        onResizeBlock={onResizeBlock}
        onUpdateBlock={onUpdateBlock}
      />

      {/* Variant Picker Modal (legacy, still used for section structure) */}
      {variantPickerTarget &&
        (() => {
          const targetSection = localSections.find((s) => s.id === variantPickerTarget)
          if (!targetSection) return null
          return (
            <VariantPicker
              sectionKey={targetSection.section_key ?? ''}
              currentVariant={targetSection.variant_key ?? ''}
              onSelect={onVariantSelected}
              onClose={onCloseVariantPicker}
            />
          )
        })()}

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div className="editor-command-backdrop" onClick={onCloseShortcuts} role="presentation">
          <div
            className="editor-modal"
            style={{ marginTop: '15vh', width: 380, maxHeight: '60vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}
            >
              <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--editor-text)' }}>
                Keyboard Shortcuts
              </h2>
              <button
                className="editor-btn editor-btn-ghost editor-btn-icon"
                onClick={onCloseShortcuts}
              >
                ✕
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                ['⌘ K', 'Command palette'],
                ['⌘ S', 'Save changes'],
                ['⌘ Z', 'Undo'],
                ['⌘ ⇧ Z', 'Redo'],
                ['⌘ ⌫', 'Delete selected block'],
                ['⌘ D', 'Duplicate selected block'],
                ['⌘ C / V', 'Copy / Paste block'],
                ['⌘ A', 'Select all blocks'],
                ['⌘ G', 'Toggle grid overlay'],
                ['Esc', 'Deselect / Close modal'],
                ['?', 'Show this dialog'],
              ].map(([key, desc]) => (
                <div
                  key={key}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span style={{ fontSize: 12, color: 'var(--editor-text-muted)' }}>{desc}</span>
                  <span className="editor-kbd">{key}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
